import React from "react";
import { motion } from "framer-motion";
import { 
  Target, 
  CheckCircle2, 
  Circle, 
  Zap, 
  Gamepad2, 
  Share2, 
  Pickaxe,
  TrendingUp,
  ChevronRight,
  Trophy
} from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData } from "../../types";
import useThemeHook from "../hooks/useThemeHook";
import { THEME_COLORS } from "../themes/DarkLight";

const DailyQuests: React.FC = () => {
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  // Mission progression based on user fields
  const missions = [
    {
      id: "mine_twice",
      title: "Active Miner",
      description: "Complete 2 mining sessions",
      progress: user?.miningSessionsCount ? (user.miningSessionsCount % 2 === 0 && user.miningSessionsCount > 0 ? 2 : 1) : 0,
      target: 2,
      reward: "0.50 ATH",
      icon: <Pickaxe size={18} className="text-blue-500" />,
      completed: user?.miningSessionsCount ? user.miningSessionsCount % 2 === 0 && user.miningSessionsCount > 0 : false
    },
    {
      id: "play_games",
      title: "Game Master",
      description: "Play any game 3 times",
      progress: Math.min(user?.dailyStats?.gamesPlayed || 0, 3),
      target: 3,
      reward: "0.50 ATH",
      icon: <Gamepad2 size={18} className="text-purple-500" />,
      completed: (user?.dailyStats?.gamesPlayed || 0) >= 3
    },
    {
      id: "share_app",
      title: "Community Growth",
      description: "Share your referral link",
      progress: Math.min(user?.dailyStats?.sharesCount || 0, 1),
      target: 1,
      reward: "0.50 ATH",
      icon: <Share2 size={18} className="text-emerald-500" />,
      completed: (user?.dailyStats?.sharesCount || 0) >= 1
    }
  ];

  const completedCount = missions.filter(m => m.completed).length;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-500/10 rounded-lg">
            <Target className="text-indigo-600" size={18} />
          </div>
          <h3 className={`font-black text-lg ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
            Daily Quests
          </h3>
        </div>
        <div className="flex items-center gap-1 bg-indigo-600/10 px-2 py-1 rounded-full">
           <Zap className="text-indigo-600" size={12} fill="currentColor" />
           <span className="text-[10px] font-black text-indigo-600">
             {completedCount}/{missions.length} DONE
           </span>
        </div>
      </div>

      <div className="space-y-3">
        {missions.map((mission, idx) => (
          <motion.div
            key={mission.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`
              p-4 rounded-2xl border flex items-center gap-4
              ${theme === "dark" ? "bg-slate-800/40 border-slate-700/50" : "bg-white border-gray-200 shadow-sm"}
            `}
          >
            <div className={`p-3 rounded-xl ${theme === "dark" ? "bg-slate-700/50" : "bg-indigo-50"}`}>
              {mission.icon}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className={`font-bold text-sm truncate ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                  {mission.title}
                </h4>
                <span className="text-[10px] font-black text-indigo-600">
                  +{mission.reward}
                </span>
              </div>
              
              <p className={`text-[10px] mb-2 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                {mission.description}
              </p>
              
              <div className="flex items-center gap-3">
                <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${theme === "dark" ? "bg-slate-700" : "bg-gray-200"}`}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(mission.progress / mission.target) * 100}%` }}
                    className="h-full bg-indigo-600"
                  />
                </div>
                <span className={`text-[10px] font-bold ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                  {mission.progress}/{mission.target}
                </span>
              </div>
            </div>
            
            <div className="shrink-0">
              {mission.completed ? (
                <CheckCircle2 className="text-emerald-500" size={20} />
              ) : (
                <ChevronRight className="text-slate-300" size={20} />
              )}
            </div>
          </motion.div>
        ))}

        {/* Bonus Reward Info */}
        <div className={`
          p-3 rounded-xl border border-dashed flex items-center justify-between
          ${theme === "dark" ? "bg-amber-500/5 border-amber-500/20" : "bg-amber-50 border-amber-200"}
        `}>
           <div className="flex items-center gap-2">
             <Trophy className="text-amber-500" size={16} />
             <span className={`text-[10px] font-bold ${theme === "dark" ? "text-amber-200" : "text-amber-700"}`}>
               Complete all to get "Perfect Day" bonus
             </span>
           </div>
           <span className={`text-[10px] font-black ${theme === "dark" ? "text-amber-200" : "text-amber-700"}`}>
             +1.00 ATH
           </span>
        </div>
      </div>
    </div>
  );
};

export default DailyQuests;
